import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BeforeAfterSlider } from '@/components/custom/BeforeAfterSlider';
import { CaseStudyCard } from '@/components/custom/CaseStudyCard';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const Galerie = () => {
  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useState(null);

  // ⚠️ IMAGES PLACEHOLDER - Solution 1: CMS Admin (Phase 3)
  // Ces images seront gérables via le back-office pour upload de vos vraies photos
  // Solution 2: Images améliorées - AVANT: salles de bain datées / APRÈS: modernes premium

  const projects = [
    {
      id: 1,
      beforeImage: 'https://images.unsplash.com/photo-1619191949584-0d9a2c9447e1?w=800',
      afterImage: 'https://images.unsplash.com/photo-1756079664354-34944e001f6d?w=800',
      location: 'Genève',
      duration: '3 semaines',
      budget: '18’000-25’000 CHF',
      surface: '7',
      projectType: 'Rénovation complète',
      category: 'complete'
    },
    {
      id: 2,
      beforeImage: 'https://images.unsplash.com/photo-1542229336-fcba447980f9?w=800',
      afterImage: 'https://images.unsplash.com/photo-1771681278446-6f8eb0c7323f?w=800',
      location: 'Lausanne',
      duration: '2 semaines',
      budget: '12’000-18’000 CHF',
      surface: '5',
      projectType: 'Rénovation partielle',
      category: 'partielle'
    },
    {
      id: 3,
      beforeImage: 'https://images.unsplash.com/photo-1723257892345-098a2b0e3748?w=800',
      afterImage: 'https://images.unsplash.com/photo-1750036015902-c6f5ebca924e?w=800',
      location: 'Montreux',
      duration: '10 jours',
      budget: '8’000-12’000 CHF',
      surface: '4',
      projectType: 'Carrelage',
      category: 'carrelage'
    },
    {
      id: 4,
      beforeImage: 'https://images.unsplash.com/photo-1603399587513-136aa9398303?w=800',
      afterImage: 'https://images.unsplash.com/photo-1564540574859-0dfb1f46e9b6?w=800',
      location: 'Fribourg',
      duration: '4 semaines',
      budget: '22’000-30’000 CHF',
      surface: '9',
      projectType: 'Rénovation complète',
      category: 'complete'
    },
    {
      id: 5,
      beforeImage: 'https://images.unsplash.com/photo-1594026112284-02bb6f3352fe?w=800',
      afterImage: 'https://images.unsplash.com/photo-1568564485646-8387cc48ed0e?w=800',
      location: 'Neuchâtel',
      duration: '1 semaine',
      budget: '5’000-8’000 CHF',
      surface: '6',
      projectType: 'Sanitaires',
      category: 'sanitaires'
    },
    {
      id: 6,
      beforeImage: 'https://images.unsplash.com/photo-1507652313519-d4e9174996dd?w=800',
      afterImage: 'https://images.unsplash.com/photo-1771681278446-6f8eb0c7323f?w=800',
      location: 'Sion',
      duration: '2 semaines',
      budget: '10’000-15’000 CHF',
      surface: '5.5',
      projectType: 'Rénovation partielle',
      category: 'partielle'
    }
  ];

  return (
    <div data-testid="gallery-page">
      {/* Hero */}
      <section className="py-14 sm:py-18 lg:py-24 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-12"
          >
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2B2B2B] mb-6"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              Galerie Avant / Après
            </h1>
            <p className="text-xl text-slate-600">
              Découvrez nos réalisations et laissez-vous inspirer par les transformations
              que nous avons réalisées en Suisse romande.
            </p>
          </motion.div>

          {/* Featured Before/After */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-16"
          >
            <BeforeAfterSlider
              beforeImage="https://images.unsplash.com/photo-1619191949584-0d9a2c9447e1?w=1200"
              afterImage="https://images.unsplash.com/photo-1756079664354-34944e001f6d?w=1200"
              className="max-w-4xl mx-auto"
            />
          </motion.div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-14 sm:py-18 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="all" className="w-full" data-testid="gallery-filter-tabs">
            <TabsList className="mb-12 flex-wrap justify-center h-auto gap-2">
              <TabsTrigger value="all">Tous les projets</TabsTrigger>
              <TabsTrigger value="complete">Rénovation complète</TabsTrigger>
              <TabsTrigger value="partielle">Rénovation partielle</TabsTrigger>
              <TabsTrigger value="carrelage">Carrelage</TabsTrigger>
              <TabsTrigger value="sanitaires">Sanitaires</TabsTrigger>
            </TabsList>

            <TabsContent value="all" className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project) => (
                <CaseStudyCard
                  key={project.id}
                  {...project}
                  onClick={() => setSelectedProject(project)}
                />
              ))}
            </TabsContent>

            <TabsContent value="complete" className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.filter(p => p.category === 'complete').map((project) => (
                <CaseStudyCard
                  key={project.id}
                  {...project}
                  onClick={() => setSelectedProject(project)}
                />
              ))}
            </TabsContent>

            <TabsContent value="partielle" className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.filter(p => p.category === 'partielle').map((project) => (
                <CaseStudyCard
                  key={project.id}
                  {...project}
                  onClick={() => setSelectedProject(project)}
                />
              ))}
            </TabsContent>

            <TabsContent value="carrelage" className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.filter(p => p.category === 'carrelage').map((project) => (
                <CaseStudyCard
                  key={project.id}
                  {...project}
                  onClick={() => setSelectedProject(project)}
                />
              ))}
            </TabsContent>

            <TabsContent value="sanitaires" className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.filter(p => p.category === 'sanitaires').map((project) => (
                <CaseStudyCard
                  key={project.id}
                  {...project}
                  onClick={() => setSelectedProject(project)}
                />
              ))}
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 sm:py-18 lg:py-24 bg-[#2B2B2B] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Prêt pour votre transformation?
          </h2>
          <p className="text-xl text-slate-300">
            Demandez un devis personnalisé et rejoignez nos clients satisfaits.
          </p>
          <Button 
            onClick={() => navigate('/devis')}
            size="lg"
            className="bg-[#336DBF] hover:bg-[#2F62AA] text-white font-medium px-8 py-6"
          >
            Demander un devis gratuit
          </Button>
        </div>
      </section>

      {/* Project Detail Dialog */}
      <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
        <DialogContent className="max-w-4xl">
          {selectedProject && (
            <div className="space-y-6">
              <BeforeAfterSlider
                beforeImage={selectedProject.beforeImage}
                afterImage={selectedProject.afterImage}
              />
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-2xl font-bold text-[#2B2B2B] mb-4">{selectedProject.projectType}</h3>
                  <div className="space-y-2 text-sm">
                    <p className="text-slate-600"><strong>Localisation:</strong> {selectedProject.location}</p>
                    <p className="text-slate-600"><strong>Durée:</strong> {selectedProject.duration}</p>
                    <p className="text-slate-600"><strong>Surface:</strong> {selectedProject.surface} m²</p>
                    <p className="text-slate-600"><strong>Budget:</strong> {selectedProject.budget}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  <Button 
                    onClick={() => {
                      setSelectedProject(null);
                      navigate('/devis');
                    }}
                    className="w-full bg-[#336DBF] hover:bg-[#2F62AA] text-white"
                  >
                    Demander un devis similaire
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Galerie;